import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Container,
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Modal,
  Offcanvas,
  Accordion,
  Badge,
  Spinner,
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getReceiptsReportToExport } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { unparse } from "papaparse";
import "./CreateInvoice.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function AllocationReport() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [practiceList, setPracticeList] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  const [page, setPage] = useState(1);
  const limit = 20;

  const [showInfo, setShowInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [selectedMethods, setSelectedMethods] = useState([]);
  const [tmpMethods, setTmpMethods] = useState([]);

  const [selectedPractitioners, setSelectedPractitioners] = useState([]);
  const [tmpPractitioners, setTmpPractitioners] = useState([]);

  const navigate = useNavigate();
  const query = useQuery();

  const currency = new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  });

  // Init dates (last 7 days)
  useEffect(() => {
    const today = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const iso = (d) => d.toISOString().slice(0, 10);
    setStartDate(iso(start));
    setEndDate(iso(today));
    setTmpStartDate(iso(start));
    setTmpEndDate(iso(today));
  }, []);

  // Fetch practices
  useEffect(() => {
    async function fetchPractices() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
      } catch (e) {
        console.error("Error fetching practices:", e);
        setPracticeList([]);
      }
    }
    fetchPractices();
  }, []);

  // Fetch receipts using new API
  useEffect(() => {
    if (!startDate || !endDate || practiceList.length === 0) return;

    async function fetchReceipts() {
      try {
        setLoading(true);
        const practiceNums = practiceList.map((p) => p.practiceNumber);

        const response = await getReceiptsReportToExport(
          startDate,
          endDate,
          practiceNums,
          null // no accountId filter
        );

        console.log("Fetched receipts report data:", response);

        // Map the new structure to our component's format
        const rows = (response.data || []).map((item) => ({
          receiptDate: item.receiptDate || "",
          receiptNumber: item.receiptNumber || "",
          method: item.receiptMethod || "",
          receiptReference: item.receiptReference || "",
          invoiceNumber: item.invoiceNumber || "",
          invoiceDate: item.invoiceDateFromReceipt || "",
          practitionerFirstName: item.practitionerFirstName || "",
          practitionerLastName: item.practitionerLastName || "",
          receiptAmount: Number(item.paymentAmount) || 0,
          amountExclVAT: Number(item.amountExclVAT) || 0,
          vatAmount: Number(item.vatAmount) || 0,
          practiceName: item.practiceName || "",
          invoiceId: item.invoiceId,
          accountId: item.accountId,
          accountName: item.accountName || "",
          paymentSource: item.paymentSource || "",
          _rowId: `${item.receiptId}-${item.invoiceId}-${item.receiptNumber}`,
        }));

        setReceipts(rows);
      } catch (e) {
        console.error("Error fetching receipts:", e);
        setReceipts([]);
      } finally {
        setLoading(false);
      }
    }

    fetchReceipts();
  }, [startDate, endDate, practiceList]);

  const allMethods = useMemo(() => {
    const s = new Set();
    (receipts || []).forEach((r) => {
      const m = (r.method || "").trim();
      if (m) s.add(m);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [receipts]);

  const allPractitioners = useMemo(() => {
    const s = new Set();
    (receipts || []).forEach((r) => {
      const name =
        `${r.practitionerFirstName} ${r.practitionerLastName}`.trim();
      if (name) s.add(name);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [receipts]);

  // Practices list for filter panel
  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList]
  );

  // Filter by practice + date
  const filteredRows = useMemo(() => {
    const s = startDate ? new Date(startDate) : null;
    const e = endDate ? new Date(endDate) : null;

    return receipts.filter((row) => {
      const practiceOK =
        selectedPractices.length === 0 ||
        (row.practiceName && selectedPractices.includes(row.practiceName));

      const methodOK =
        selectedMethods.length === 0 ||
        (row.method && selectedMethods.includes(row.method));

      const practitionerName =
        `${row.practitionerFirstName} ${row.practitionerLastName}`.trim();
      const practitionerOK =
        selectedPractitioners.length === 0 ||
        selectedPractitioners.includes(practitionerName);

      const d = row.receiptDate ? new Date(row.receiptDate) : null;
      const dateOK = (!s || (d && d >= s)) && (!e || (d && d <= e));

      return practiceOK && methodOK && practitionerOK && dateOK;
    });
  }, [
    receipts,
    selectedPractices,
    selectedMethods,
    selectedPractitioners,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    setPage(1);
  }, [selectedPractices, selectedMethods, startDate, endDate, sortConfig]);

  // Sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    const next = { key, direction };
    setSortConfig(next);
    setSortConfig(next);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="fa-solid fa-arrows-up-down text-primary ms-2" />;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];
      if (sortConfig.key === "receiptAmount") {
        const numA = Number(valA) || 0;
        const numB = Number(valB) || 0;
        return sortConfig.direction === "asc" ? numA - numB : numB - numA;
      }
      return sortConfig.direction === "asc"
        ? String(valA || "").localeCompare(String(valB || ""))
        : String(valB || "").localeCompare(String(valA || ""));
    });
    return arr;
  }, [filteredRows, sortConfig]);

  // Totals
  const totalReceipts = useMemo(
    () =>
      sortedRows.reduce((sum, r) => sum + (Number(r.receiptAmount) || 0), 0),
    [sortedRows]
  );

  const totalExclVAT = useMemo(
    () =>
      sortedRows.reduce((sum, r) => sum + (Number(r.amountExclVAT) || 0), 0),
    [sortedRows]
  );

  const totalVAT = useMemo(
    () => sortedRows.reduce((sum, r) => sum + (Number(r.vatAmount) || 0), 0),
    [sortedRows]
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / limit));
  const pageStart = (page - 1) * limit;
  const pagedRows = sortedRows.slice(pageStart, pageStart + limit);

  // CSV export
  const handleExportCSV = () => {
    const exportData = filteredRows.map((r) => ({
      ReceiptDate: r.receiptDate,
      ReceiptNumber: r.receiptNumber,
      ReceiptReference: r.receiptReference,
      MethodOfPayment: r.method,
      PaymentSource: r.paymentSource,
      InvoiceNumber: r.invoiceNumber,
      InvoiceDate: r.invoiceDate,
      AccountName: r.accountName,
      PractitionerFirstName: r.practitionerFirstName,
      PractitionerLastName: r.practitionerLastName,
      ReceivedInclVAT: r.receiptAmount,
      ReceivedExclVAT: r.amountExclVAT,
      VATAmount: r.vatAmount,
      PracticeName: r.practiceName,
    }));

    const csv = unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "allocation-report.csv";
    a.click();
  };

  // Filter panel actions
  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpMethods(selectedMethods);
    setSelectedPractitioners(tmpPractitioners);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedMethods(tmpMethods);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedMethods([]);
    setSelectedPractitioners([]);
    const today = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const iso = (d) => d.toISOString().slice(0, 10);
    const s = iso(start);
    const e = iso(today);
    setStartDate(s);
    setEndDate(e);
    setTmpPractices([]);
    setTmpMethods([]);
    setTmpStartDate(s);
    setTmpEndDate(e);
  };

  // Determine if any of the selected practices are VAT registered
  const hasVATRegisteredPractice = useMemo(() => {
    // If no specific practices selected, check all in list
    const relevant = selectedPractices.length
      ? practiceList.filter((p) => selectedPractices.includes(p.practiceName))
      : practiceList;

    // True if any have a VATNumber field (non-empty string)
    return relevant.some((p) => p.vatNumber && p.vatNumber.trim() !== "");
  }, [practiceList, selectedPractices]);

  // little badge count helper
  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (startDate && endDate ? 1 : 0) +
    (selectedMethods.length ? 1 : 0) +
    (selectedPractitioners.length ? 1 : 0);

  return (
    <Container fluid className="pt-2">
      <Row className="mb-3 align-items-center">
        <Col md={4}>
        <span className="text-primary fw-semibold">Total Receipts:</span>
          <Table bordered size="sm" className="mb-0 text-center align-middle">
            <thead className="table-primary">
              <tr>
               
                {hasVATRegisteredPractice && (
                  <>
                    <th className="fw-semibold text-primary" style={{ width: "33%" }}>Excluding VAT</th>
                    <th className="fw-semibold text-primary" style={{ width: "33%" }}>VAT</th>
                  </>

                )}
                 <th className="fw-semibold text-primary" style={{ width: "33%" }}>
                  Including VAT
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                
                {hasVATRegisteredPractice && (
                  <>
                    <td>
                      <strong>{currency.format(totalExclVAT)}</strong>
                    </td>
                    <td>
                      <strong>{currency.format(totalVAT)}</strong>
                    </td>
                  </>
                )}
                <td>
                  <strong>{currency.format(totalReceipts)}</strong>
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>

        <Col md={8} className="d-flex gap-2 flex-wrap justify-content-end">
          <Button
            variant="primary"
            onClick={openFilters}
            style={{ width: "120px" }}
          >
            <i className="fa-solid fa-filter me-2" />
            Filter{" "}
            {activeCount > 0 && (
              <Badge className="ms-2" bg="light" text="dark">
                {activeCount}
              </Badge>
            )}
          </Button>
          <Button
            variant="outline-danger"
            onClick={clearFilters}
            style={{ width: "120px" }}
            disabled={
              selectedPractices.length === 0 &&
              selectedMethods.length === 0 &&
              !startDate &&
              !endDate
            }
          >
            <i className="fa-solid fa-trash me-2" />
            Filters
          </Button>

          <div className="d-flex align-items-center gap-2 ms-2">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <div className="text-nowrap small">
              Page {page} of {totalPages}
            </div>
            <Button
              variant="primary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>

          <ButtonGroup className="ms-1 gap-1">
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Export to CSV</Tooltip>}
            >
              <Button variant="outline-secondary" onClick={handleExportCSV}>
                <i className="fa-solid fa-file-export" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Info</Tooltip>}
            >
              <Button
                variant="outline-secondary"
                onClick={() => setShowInfo(true)}
              >
                <i className="fa-solid fa-circle-info" />
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading Allocations...</p>
        </div>
      ) : pagedRows.length === 0 ? (
        <p>
          No receipts found. The default filter is for the last 7 days (by
          receipt date).
        </p>
      ) : (
        <Table bordered hover responsive className="fixed-table allocation-report-table">
          <thead className="table-primary text-center align-middle ms-1 me-1">
            <tr>
              <th style={{ width: "3%" }} className="text-primary fw-semibold">
                #
              </th>
              <th
                style={{ cursor: "pointer", width: "7%" }}
                onClick={() => handleSort("receiptDate")}
                className="text-primary fw-semibold"
              >
                Date {renderSortIcon("receiptDate")}
              </th>
              <th
                style={{ cursor: "pointer", width: "10%" }}
                onClick={() => handleSort("receiptNumber")}
                className="text-primary fw-semibold"
              >
                Receipt # {renderSortIcon("receiptNumber")}
              </th>
              <th
                style={{ cursor: "pointer", width: "7%" }}
                onClick={() => handleSort("receiptReference")}
                className="text-primary fw-semibold"
              >
                Ref {renderSortIcon("receiptReference")}
              </th>
              <th
                style={{ cursor: "pointer", width: "10%" }}
                onClick={() => handleSort("method")}
                className="text-primary fw-semibold"
              >
                Method {renderSortIcon("method")}
              </th>
              <th
                style={{ cursor: "pointer", width: "7%" }}
                onClick={() => handleSort("invoiceNumber")}
                className="text-primary fw-semibold"
              >
                Inv # {renderSortIcon("invoiceNumber")}
              </th>
              <th
                style={{ cursor: "pointer", width: "10%" }}
                onClick={() => handleSort("invoiceDate")}
                className="text-primary fw-semibold"
              >
                Inv Date {renderSortIcon("invoiceDate")}
              </th>
              <th
                style={{ cursor: "pointer", width: "14%" }}
                onClick={() => handleSort("accountName")}
                className="text-primary fw-semibold"
              >
                Account {renderSortIcon("accountName")}
              </th>
              <th
                style={{ cursor: "pointer", width: "12%" }}
                onClick={() => handleSort("practitionerFirstName")}
                className="text-primary fw-semibold"
              >
                Practitioner {renderSortIcon("practitionerFirstName")}
              </th>

              {hasVATRegisteredPractice && (
                <>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("amountExclVAT")}
                    className="text-primary fw-semibold text-end"
                  >
                    Excl. VAT {renderSortIcon("amountExclVAT")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("vatAmount")}
                    className="text-primary fw-semibold text-end"
                  >
                    VAT {renderSortIcon("vatAmount")}
                  </th>
                </>
              )}
              <th
                style={{ cursor: "pointer", width: "8%" }}
                onClick={() => handleSort("receiptAmount")}
                className="text-primary fw-semibold text-end"
              >
                Received {renderSortIcon("receiptAmount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, idx) => {
              const rowNum = (page - 1) * limit + idx + 1;
              return (
                <tr
                  key={row._rowId}
                  style={{ cursor: row.invoiceId ? "pointer" : "default" }}
                  onClick={() =>
                    row.invoiceId &&
                    navigate(`/invoices/posted/${row.invoiceId}`)
                  }
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "")
                  }
                >
                  <td>{rowNum}</td>
                  <td>
                    {row.receiptDate
                      ? new Date(row.receiptDate).toLocaleDateString()
                      : ""}
                  </td>
                  <td>{row.receiptNumber}</td>
                  <td>{row.receiptReference}</td>
                  <td>{row.method}</td>
                  <td>{row.invoiceNumber}</td>
                  <td>
                    {row.invoiceDate
                      ? new Date(row.invoiceDate).toLocaleDateString()
                      : ""}
                  </td>
                  <td>{row.accountName}</td>
                  <td>
                    {row.practitionerFirstName} {row.practitionerLastName}
                  </td>
                  

                  {hasVATRegisteredPractice && (
                    <>
                      <td className="text-end">
                        {currency.format(Number(row.amountExclVAT) || 0)}
                      </td>
                      <td className="text-end">
                        {currency.format(Number(row.vatAmount) || 0)}
                      </td>
                    </>
                  )}
                  <td className="text-end fw-bold">
                    {currency.format(Number(row.receiptAmount) || 0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal show={showInfo} onHide={() => setShowInfo(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Allocation Report (Receipts)</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">
          One row per <strong>receipt payment</strong> allocated to an invoice.
          Filters apply to the <strong>receipt date</strong> and selected
          practices. Totals reflect all filtered rows. CSV export includes all
          displayed columns.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowInfo(false)}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="test-primary d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-3">
            <h6 className="mb-1 text-primary fw-normal ms-3 mb-2">
              Receipt date range
            </h6>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>

          <Accordion alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices(allPractices)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  {allPractices.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`practice-${name}`}
                      label={name}
                      checked={tmpPractices.includes(name)}
                      onChange={() =>
                        setTmpPractices((arr) =>
                          arr.includes(name)
                            ? arr.filter((v) => v !== name)
                            : [...arr, name]
                        )
                      }
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Payment method</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpMethods(allMethods)}
                    disabled={allMethods.length === 0}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpMethods([])}
                  >
                    Clear
                  </Button>
                </div>

                {allMethods.length === 0 ? (
                  <div className="text-muted small">
                    No methods in the current result set.
                  </div>
                ) : (
                  <div
                    className="d-flex flex-column"
                    style={{ maxHeight: 240, overflowY: "auto" }}
                  >
                    {allMethods.map((m) => (
                      <Form.Check
                        key={m}
                        type="checkbox"
                        id={`method-${m}`}
                        label={m}
                        checked={tmpMethods.includes(m)}
                        onChange={() =>
                          setTmpMethods((arr) =>
                            arr.includes(m)
                              ? arr.filter((v) => v !== m)
                              : [...arr, m]
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>Practitioners</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractitioners(allPractitioners)}
                    disabled={allPractitioners.length === 0}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractitioners([])}
                  >
                    Clear
                  </Button>
                </div>

                {allPractitioners.length === 0 ? (
                  <div className="text-muted small">
                    No practitioners in the current result set.
                  </div>
                ) : (
                  <div
                    className="d-flex flex-column"
                    style={{ maxHeight: 240, overflowY: "auto" }}
                  >
                    {allPractitioners.map((name) => (
                      <Form.Check
                        key={name}
                        type="checkbox"
                        id={`practitioner-${name}`}
                        label={name}
                        checked={tmpPractitioners.includes(name)}
                        onChange={() =>
                          setTmpPractitioners((arr) =>
                            arr.includes(name)
                              ? arr.filter((v) => v !== name)
                              : [...arr, name]
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpMethods([]);
                setTmpStartDate("");
                setTmpEndDate("");
                setTmpPractitioners([]);
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
